"use client";

/**
 * P13-05 · The lost-my-confirmation-email form.
 *
 * Follows the contact form's conventions — success REPLACES the form and takes
 * focus, `aria-busy` in flight — for the reason recorded there: a confirmation
 * that mounts without moving focus drops a keyboard user to `<body>` with no
 * announcement that anything happened.
 *
 * What is different here is the copy, and it is load-bearing. The success
 * message must be the SAME whether or not anything matched, because the server
 * deliberately answers identically either way (see `access-link.ts`). So it
 * says "if these match an order, we've sent the link" — a sentence that is true
 * in both cases and tells a guesser nothing. Writing the warmer, more natural
 * "check your inbox!" would quietly turn this form into an oracle for which
 * order numbers exist.
 */
import * as React from "react";

import { api } from "~/trpc/react";
import { useTranslator } from "~/i18n/provider";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]";
const labelClass = "flex flex-col gap-1 text-sm font-medium text-fg";

export function OrderLookupForm() {
  const t = useTranslator();
  const uid = React.useId();
  const errorId = `${uid}-error`;

  const [orderNumber, setOrderNumber] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [clientError, setClientError] = React.useState<string | null>(null);

  const request = api.orders.requestAccessLink.useMutation();

  const doneRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (request.isSuccess) doneRef.current?.focus();
  }, [request.isSuccess]);

  if (request.isSuccess) {
    return (
      <div
        ref={doneRef}
        tabIndex={-1}
        role="status"
        className="rounded-md border border-border bg-surface-2 px-4 py-6 text-sm text-fg outline-none"
      >
        <p className="font-medium">{t("orderLookup.sentHeading")}</p>
        <p className="mt-1 text-fg-muted">{t("orderLookup.sentBody")}</p>
      </div>
    );
  }

  return (
    <form
      className="flex max-w-md flex-col gap-4"
      aria-busy={request.isPending}
      onSubmit={(e) => {
        e.preventDefault();
        if (!orderNumber.trim() || !EMAIL_RE.test(email.trim())) {
          setClientError(t("orderLookup.invalid"));
          return;
        }
        setClientError(null);
        request.mutate({
          orderNumber: orderNumber.trim(),
          email: email.trim(),
        });
      }}
    >
      <label className={labelClass} htmlFor={`${uid}-number`}>
        {t("orderLookup.orderNumber")}
        <input
          id={`${uid}-number`}
          className={inputClass}
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          autoComplete="off"
          required
        />
      </label>

      <label className={labelClass} htmlFor={`${uid}-email`}>
        {t("orderLookup.email")}
        <input
          id={`${uid}-email`}
          className={inputClass}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
      </label>

      {(clientError ?? request.error) && (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {clientError ?? t("orderLookup.failed")}
        </p>
      )}

      <button
        type="submit"
        disabled={request.isPending}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-fg disabled:opacity-60"
      >
        {request.isPending ? t("orderLookup.sending") : t("orderLookup.submit")}
      </button>
    </form>
  );
}
